import React from 'react';
import { Gap } from 'lib/components/styles/layout';
import { useFormikContext } from 'formik';
import { ILandingPageBuilderForm } from '../../types';
import {
  LP_BUILDER_FORM_MODE,
  LP_DESIGN_SOCIAL_MEDIA,
  LpBuilderFormMode,
} from '../../constants';
import { FormikRadioField } from 'lib/components/formik/FormikRadioField';
import { SocialIcon } from '../../helpers/socialIcons';
import { FormikSwitchField } from 'lib/components/formik/FormikSwitchField';
import { useCovideoTheme } from 'react-covideo-common';
import { FormSection } from './shared/FormSection';
import { FormContainer } from './shared/FormContainer';
import { ILandingPageBuilderBulkEditFormData } from '../../helpers/getInitialLandingPageValuesForBulkEdit';

export const generateFacebookIcons = Object.values(LP_DESIGN_SOCIAL_MEDIA).map(
  design => {
    const icon = (
      <SocialIcon
        icon='facebook'
        design={design}
        svgSize={24}
        useOutlinedIcon
      />
    );
    return {
      icon,
      value: design,
    };
  }
);

interface ISocialIconsFormProps {
  mode: LpBuilderFormMode;
}

export const SocialIconsForm = ({ mode }: ISocialIconsFormProps) => {
  const themes = useCovideoTheme();
  const { values, setFieldValue } = useFormikContext<
    ILandingPageBuilderForm | ILandingPageBuilderBulkEditFormData
  >();
  const isBulkEditMode = mode === LP_BUILDER_FORM_MODE.BULK_EDIT;

  const handleSocialIconClick = (value: LP_DESIGN_SOCIAL_MEDIA) => {
    const isAlreadySelectedIcon = values.socialMediaIconType === value;
    // if the clicked icon is already selected and we are in bulk edit mode, deselect it
    if (isAlreadySelectedIcon && isBulkEditMode) {
      setFieldValue('socialMediaIconType', null);
      setFieldValue('showSocialMediaIcons', 0);
      return;
    }
    setFieldValue('socialMediaIconType', value);
  };

  const handleShowSocialMediaIconsChange = () => {
    const newValue = values.showSocialMediaIcons ? 0 : 1;
    // if we are in bulk edit mode, set the socialMediaIconType to default WHITE_SQUARE when enabling icons
    // otherwise set it to null — because we want to deselect showing icons if icons are deselected
    if (isBulkEditMode) {
      const value = newValue === 1 ? LP_DESIGN_SOCIAL_MEDIA.WHITE_SQUARE : null;
      setFieldValue('socialMediaIconType', value);
    }
    setFieldValue('showSocialMediaIcons', newValue);
  };

  return (
    <FormContainer title='Social Icons'>
      <FormSection title='Icons'>
        <Gap width='100%' justifyContent='space-between'>
          <FormikSwitchField
            extendStyles={{
              wrapper: { margin: '0 0 16px 0' },
              labelStyles: {
                fontSize: '15px',
                lineHeight: '24px',
                fontWeight: 500,
                color: themes.colors.neutral[100],
              },
            }}
            name='showSocialMediaIcons'
            label='Show Icons'
            onChange={handleShowSocialMediaIconsChange}
          />
        </Gap>
      </FormSection>

      {values.showSocialMediaIcons ? (
        <FormSection title='Social Media icon design'>
          <Gap flexDirection='row' width='100%' gap='16px 60px'>
            {generateFacebookIcons.map((option, index) => {
              return (
                <Gap key={`radio-${index}`}>
                  <FormikRadioField
                    name='socialMediaIconType'
                    label={option.icon}
                    value={option.value}
                    extendStyles={{ labelWrapper: { marginLeft: '36px' } }}
                    onClick={() => handleSocialIconClick(option.value)}
                  />
                </Gap>
              );
            })}
          </Gap>
        </FormSection>
      ) : (
        <></>
      )}
    </FormContainer>
  );
};
